import { IconUserPlus } from "@tabler/icons-react";
import { createFileRoute } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { useOrgUsersPage } from "@/common/artemis-operations/members";
import {
	BaseContainer,
	BasePageContainer,
	CustomIcon,
} from "@/components/base";
import { UserTable } from "@/components/users/user-table";
import { OrganizationAdmins } from "@/components/workspaces/organization-admins";
import { useActions } from "@/contexts/actions-context";
import { usePageTitle } from "@/contexts/page-title-context";

export const Route = createFileRoute("/_authenticated/$orgSlug/admin/users")({
	component: AdminUsersPage,
});

function AdminUsersPage() {
	const { setTitle } = usePageTitle();
	useEffect(() => setTitle("Users"), [setTitle]);
	const { setActions } = useActions();
	const [addAdminOpen, setAddAdminOpen] = useState(false);

	useEffect(() => {
		setActions([
			{
				type: "button",
				id: "add-admin",
				tooltip: "Add admin",
				icon: <CustomIcon icon={IconUserPlus} />,
				onClick: () => setAddAdminOpen(true),
			},
		]);
		return () => setActions([]);
	}, [setActions]);
	// Server-side search + cursor paging. Cursor stack: each entry is the `after`
	// used to reach that page (undefined = first page); current page is the last.
	const [search, setSearch] = useState("");
	const [cursors, setCursors] = useState<(string | undefined)[]>([undefined]);
	const usersPage = useOrgUsersPage(search, cursors[cursors.length - 1]);

	function changeSearch(next: string) {
		setSearch(next);
		setCursors([undefined]);
	}

	return (
		<BasePageContainer>
			<OrganizationAdmins
				addOpen={addAdminOpen}
				onCloseAdd={() => setAddAdminOpen(false)}
			/>

			<BaseContainer title="Users">
				<UserTable
					data={usersPage.data?.nodes ?? []}
					loading={usersPage.isFetching}
					search={search}
					onSearchChange={changeSearch}
					pageSize={50}
					totalCount={usersPage.data?.totalCount ?? 0}
					pageIndex={cursors.length - 1}
					hasNextPage={usersPage.data?.pageInfo.hasNextPage ?? false}
					hasPreviousPage={cursors.length > 1}
					onNextPage={() => {
						const cursor = usersPage.data?.pageInfo.endCursor;
						if (cursor) setCursors((c) => [...c, cursor]);
					}}
					onPreviousPage={() =>
						setCursors((c) => (c.length > 1 ? c.slice(0, -1) : c))
					}
				/>
			</BaseContainer>
		</BasePageContainer>
	);
}
